import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatosContacto, Domicilio, MedioPago, Persona, Poliza, Riesgo } from '../../model/poliza.model';
import { DatosDomicilioConfig, DatosPersonalesConfig, ModoEdicion } from '../../../ui/model/catalogo-config.model';
import { CatalogoProtocolo, FraccionamientoCatalogo } from '../../model/catalogo.model';
import { FlujoPolizaNavigationComponent } from '../base/flujo-poliza-navigation.component';
import { Router } from '@angular/router';
import { PolizaStoreService } from '../../services/store/poliza-store.service';
import { CatalogoSvgStoreService } from '../../services/store/catalogo-svg-store.service';
import { PolizaApiService } from '../../services/api/poliza-api.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GlobalMessageHandlerService } from '../../services/util/global-message-handler.service';
import { CaCardComponent } from '@global-front-components/ui/card';
import { DatosPersonalesComponent } from '../datos-personales/datos-personales.component';
import { DatosDomicilioComponent } from '../datos-domicilio/datos-domicilio.component';
import { DatosPagoComponent } from '../datos-pago/datos-pago.component';
import { IdiomasComponent } from '../idiomas/idiomas.component';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { CaIcon } from '@global-front-components/ui/form-field';
import { DatosContactoComponent } from '../datos-contacto/datos-contacto.component';
import { DatosEconomicosComponent } from '../datos-economicos/datos-economicos.component';

@Component({
	selector: 'app-contratar-poliza',
	standalone: true,
	imports: [
		CommonModule,
		CaCardComponent,
		DatosPersonalesComponent,
		DatosDomicilioComponent,
		DatosPagoComponent,
		IdiomasComponent,
		DatosContactoComponent,
		CaButtonComponent,
		ReactiveFormsModule,
		CaIcon,
		DatosEconomicosComponent,
		TranslateModule
	],
	templateUrl: './contratar-poliza.component.html',
	styleUrls: ['./contratar-poliza.component.scss']
})
export class ContratarPolizaComponent extends FlujoPolizaNavigationComponent implements OnInit {
	contratarForm!: FormGroup;
	poliza!: Poliza<Riesgo>;
	polizaContratada!: Poliza<Riesgo>;
	tomadorConfig: DatosPersonalesConfig;
	propietarioConfig: DatosPersonalesConfig;
	conductorConfig: DatosPersonalesConfig;
	domicilioTomadorConfig: DatosDomicilioConfig;
	domicilioPropietarioConfig: DatosDomicilioConfig;
	domicilioConductorConfig: DatosDomicilioConfig;
	fraccionamientoSeleccionado: FraccionamientoCatalogo;
	mostrarPropietario = false;
	mostrarConductor = false;

	catalogo: CatalogoProtocolo;

	constructor(
		router: Router,
		polizaStore: PolizaStoreService,
		catalogoSvgStore: CatalogoSvgStoreService,
		private fb: FormBuilder,
		private polizaApiService: PolizaApiService,
		private translateService: TranslateService,
		private globalMessageHandler: GlobalMessageHandlerService
	) {
		super(router, polizaStore, catalogoSvgStore);
		this.mostrarPropietario = this.getNavigationParam<boolean>('mostrarPropietario');
		this.mostrarConductor = this.getNavigationParam<boolean>('mostrarConductor');
	}

	ngOnInit(): void {

		this.contratarForm = this.fb.group({
			datosIdiomas: this.fb.group({}),
			datosPago: this.fb.group({}),
			datosPersonalesTomador: this.fb.group({}),
			datosDomicilioTomador: this.fb.group({}),
			datosContacto: this.fb.group({})
		});

		this.catalogoSvgStore.catalogoProtocoloSubject$.subscribe((catalogo) => {
			if (catalogo) {
				this.catalogo = catalogo;
			}
		});
		this.polizaStore.polizaSubject$.subscribe((poliza: Poliza<Riesgo>) => {
			if (poliza) {
				this.poliza = poliza;
				this.tomadorConfig = {
					persona: this.poliza?.tomador || {},
					rol: 'tomador',
					modoEdicion: ModoEdicion.VALORES,
					mostrarDocumento: true,
					mostrarNombre: true,
					mostrarFechaNacimiento: false,
					mostrarSexo: false
				}
				this.domicilioTomadorConfig = {
					domicilio: this.poliza?.tomador?.domicilio || {},
					rol: 'tomador',
					modoEdicion: ModoEdicion.VALORES,
					mostrarVia: true,
					mostrarLocalidad: true,
					mostrarProvincia: true
				};
				const [primerRiesgo] = poliza.riesgos ?? [];
				if (this.mostrarPropietario && primerRiesgo?.propietario) {
					const { propietario } = primerRiesgo;
					this.propietarioConfig = {
						...this.tomadorConfig,
						persona: propietario,
						rol: 'propietario'
					};
					this.domicilioPropietarioConfig = {
						...this.domicilioTomadorConfig,
						domicilio: propietario.domicilio,
						rol: 'propietario'
					};
					this.contratarForm.addControl('datosPersonalesPropietario', this.fb.group({}));
					this.contratarForm.addControl('datosDomicilioPropietario', this.fb.group({}));
				}

				if (this.mostrarConductor && primerRiesgo?.conductor) {
					const { conductor } = primerRiesgo;
					this.conductorConfig = {
						...this.tomadorConfig,
						persona: conductor,
						rol: 'conductor'
					};
					this.domicilioConductorConfig = {
						...this.domicilioTomadorConfig,
						domicilio: conductor.domicilio,
						rol: 'conductor'
					};
					this.contratarForm.addControl('datosPersonalesConductor',this.fb.group({}));
					this.contratarForm.addControl('datosDomicilioConductor',this.fb.group({}));
				}

				if (this.catalogo && !this.fraccionamientoSeleccionado){
					this.fraccionamientoSeleccionado = this.catalogo.fraccionamientos.find(
					(f) => f.codigoFraccionamiento === this.poliza?.codigoFraccionamiento) || this.catalogo.fraccionamientos[0];
				}
			}
		});
	}

	public contratarPoliza() {
		this.poliza.codigoIdioma = this.contratarForm.get('datosIdiomas.codIdioma')?.value || 0;
		this.polizaApiService.contratarPoliza(this.poliza).subscribe({
			next: (polizaContratada: Poliza<Riesgo>) => {
				if (polizaContratada?.numeroPoliza) {
					this.polizaContratada = polizaContratada;
					this.globalMessageHandler.emitSuccess(this.translateService.instant('CONTRATAR_POLIZA.POLIZA_CONTRATADA',
						{ numeroPoliza: this.polizaContratada.numeroPoliza }));
				}
			}
		});
	}

	public onTomadorChange(persona: Persona): void {
		this.poliza.tomador = persona;
		this.poliza.pagador = persona;
		if (!this.mostrarPropietario) {
			this.poliza.riesgos.find(() => true).propietario = persona;
		}
		if (!this.mostrarConductor) {
			this.poliza.riesgos.find(() => true).conductor = persona;
			this.poliza.riesgos.find(() => true).asegurado = persona;
		}
	}

	public onDomicilioTomadorChange(domicilio: Domicilio): void {
		this.poliza.tomador.domicilio = domicilio;
	}

	public onPropietarioChange(persona: Persona): void {
		this.poliza.riesgos.find(() => true).propietario = persona;
	}

	public onDomicilioPropietarioChange(domicilio: Domicilio): void {
		this.poliza.riesgos.find(() => true).propietario.domicilio = domicilio;
	}

	public onConductorChange(persona: Persona): void {
		this.poliza.riesgos.find(() => true).conductor = persona;
		this.poliza.riesgos.find(() => true).asegurado = persona;
	}

	public onDomicilioConductorChange(domicilio: Domicilio): void {
		this.poliza.riesgos.find(() => true).conductor.domicilio = domicilio;
		this.poliza.riesgos.find(() => true).asegurado = this.poliza.riesgos.find(() => true).conductor;
	}

	public onMedioPagoChange(medioPago: MedioPago) {
		this.poliza.medioPago = medioPago;
	}

	public onDatosContactoChange(datosContacto: DatosContacto) {
		this.poliza.tomador.datosContacto = datosContacto;
	}
}
